import * as TX from "app/calcs/Accountant/Transactions/interfaces"
import { Local705FormOfPayment } from "app/calcs/customers/local705/interfaces"
import {
    ICommonPensionInputs,
    IFormOfPaymentOption,
    IMonthlyPensionPayments,
    SimplePensionFormOfPayment,
} from "app/calcs/pensions/interfaces"
import { SpousalIncomeType } from "app/calcs/pensions/spousal"
import {
    ageAtAsDuration,
    dateAtAge,
    deadBy,
    IRetirementPerson,
    isValidPerson,
    Profile,
} from "app/redux/Profile/model"
import * as utils from "app/utils"
import * as DateCalcs from "app/utils/DateCalcs"
import * as _ from "lodash"
import * as moment from "moment"
import { FandfFormOfPayment } from "../customers/fandf/interfaces"
import { IOFormOfPayment } from "../customers/IO/interfaces"
import { Local145FormOfPayment } from "../customers/local145/interfaces"
import { Local697FormOfPayment } from "../customers/local697/interfaces"
import { Local701FormOfPayment } from "../customers/local701/interfaces"
import { NEBFFormOfPayment } from "../customers/NEBF/interfaces"

const debug = utils.makeDebug(false)

const defaultMonthlyPensionPayment: IMonthlyPensionPayments = {
    amount: 0,
    kind: SimplePensionFormOfPayment.singleLifeOnly,
    beginPaymentsAge: moment.duration(65, "years"),
    totalLumpSum: 0,
    totalVestingService: 0,
    totalPensionCredits: 0,
}

// define all known forms of payments in an unordered array. Consumers may
// wish to filter out desired options and utilize a sorted view of this table
// BUT, this is the master version.
const FormOfPaymentOptions: IFormOfPaymentOption[] = [
    {
        label: "Single Life Only",
        value: SimplePensionFormOfPayment.singleLifeOnly,
        policyHolderFactor: 1,
        survivorFactor: 0,
        tooltip: "A life only payment that ends upon your death",
        needsSpouse: false,
    },
    {
        label: "Single Life Only",
        value: NEBFFormOfPayment.lifeOnly,
        policyHolderFactor: 1,
        survivorFactor: 0,
        tooltip: "A life only payment that ends upon your death",
        needsSpouse: false,
    },
    {
        label: "Single Life Only",
        value: Local697FormOfPayment.lifeOnly,
        policyHolderFactor: 1,
        survivorFactor: 0,
        tooltip: "A life only payment that ends upon your death",
        needsSpouse: false,
    },
    {
        label: "Single Life Only",
        value: FandfFormOfPayment.lifeOnly,
        policyHolderFactor: 1,
        survivorFactor: 0,
        tooltip: "A life only payment that ends upon your death",
        needsSpouse: false,
    },
    {
        label: "Single Life Only",
        value: Local701FormOfPayment.lifeOnly,
        policyHolderFactor: 1,
        survivorFactor: 0,
        tooltip: "A life only payment that ends upon your death",
        needsSpouse: false,
    },
    {
        label: "Single Life Only",
        value: IOFormOfPayment.lifeOnly,
        policyHolderFactor: 1,
        survivorFactor: 0,
        tooltip:
            "A life only payment that ends upon your death.  Under this option, all your contributions to the fund will be paid back within 5 years of commencing your benefit.",
        needsSpouse: false,
    },
    {
        label: "50% Joint & Survivor",
        value: SimplePensionFormOfPayment.FiftyPercentJointAndSurvivor,
        policyHolderFactor: 0.9,
        survivorFactor: 0.5,
        tooltip:
            "A 50% Joint & Survivor benefit pays you a reduced amount, and if you die before your spouse, he or she will receive 50% of your reduced benefit." +
            " Under this option, all your contributions to the fund will generally be paid back within 5 years of commencing your benefit.",
        needsSpouse: true,
    },
    {
        label: "100% Husband & Wife",
        value: Local697FormOfPayment.hundredPercentHusbandAndWife,
        policyHolderFactor: 1.0,
        survivorFactor: 0.5,
        tooltip:
            "A 100% husband & wife benefit pays you a reduced amount, and if you die before your spouse, he or she will receive 50% of your reduced benefit.",
        needsSpouse: true,
    },
    {
        label: "50% Joint & Survivor",
        value: FandfFormOfPayment.fiftyPercentJointAndSurvivor,
        policyHolderFactor: 0.9,
        survivorFactor: 0.5,
        tooltip:
            "A 50% Joint & Survivor benefit pays you a reduced amount, and if you die before your spouse, he or she will receive 50% of your reduced benefit.",
        needsSpouse: true,
    },
    {
        label: "50% Husband & Wife",
        value: Local701FormOfPayment.fiftyPercentHusbandAndWife,
        policyHolderFactor: 0.9,
        survivorFactor: 0.5,
        tooltip:
            "A 50% husband & wife benefit pays you a reduced amount, and if you die before your spouse, he or she will receive 50% of your reduced benefit.",
        needsSpouse: true,
    },
    {
        label: "50% Joint & Survivor with pop up",
        value: Local705FormOfPayment.fiftyPercentJointAndSurvivorWithPopup,
        policyHolderFactor: 0.85,
        survivorFactor: 0.75,
        tooltip:
            "A 50% Joint & Survivor plan, and if your spouse dies before you, you will receive your life only benefit.",
        needsSpouse: true,
    },
    {
        label: "75% Joint & Survivor",
        value: SimplePensionFormOfPayment.seventyFivePercentJointAndSurvivor,
        policyHolderFactor: 0.85,
        survivorFactor: 0.75,
        tooltip:
            "A 75% Joint & Survivor benefit pays you a reduced amount, and if you die before your spouse, he or she will receive 75% of your reduced benefit.",
        needsSpouse: true,
    },
    {
        label: "75% Joint & Survivor",
        value: FandfFormOfPayment.seventyFivePercentJointAndSurvivor,
        policyHolderFactor: 0.85,
        survivorFactor: 0.75,
        tooltip:
            "A 75% Joint & Survivor benefit pays you a reduced amount, and if you die before your spouse, he or she will receive 75% of your reduced benefit.",
        needsSpouse: true,
    },
    {
        label: "75% Husband & Wife",
        value: Local701FormOfPayment.seventyFivePercentHusbandAndwife,
        policyHolderFactor: 0.85,
        survivorFactor: 0.75,
        tooltip:
            "A 75% husband & wife benefit pays you a reduced amount, and if you die before your spouse, he or she will receive 75% of your reduced benefit.",
        needsSpouse: true,
    },
    {
        label: "75% Joint & Survivor with pop up",
        value: Local705FormOfPayment.seventyFivePercentJointAndSurvivorWithPopup,
        policyHolderFactor: 0.85,
        survivorFactor: 0.75,
        tooltip:
            "A 75% Joint & Survivor plan, and if your spouse dies before you, you will receive your life only benefit.",
        needsSpouse: true,
    },
    {
        label: "100% Joint & Survivor",
        value: SimplePensionFormOfPayment.fullJointAndSurvivor,
        policyHolderFactor: 0.8,
        survivorFactor: 1.0,
        tooltip:
            "A 100% Joint & Survivor benefit pays you a reduced amount, and if you die before your spouse, he or she will receive 100% of your reduced benefit.",
        needsSpouse: true,
    },
    {
        label: "100% Joint & Survivor",
        value: FandfFormOfPayment.fullJointAndSurvivor,
        policyHolderFactor: 0.8,
        survivorFactor: 1.0,
        tooltip:
            "A 100% Joint & Survivor benefit pays you a reduced amount, and if you die before your spouse, he or she will receive 100% of your reduced benefit.",
        needsSpouse: true,
    },
    {
        label: "100% Joint & Survivor with pop up",
        value: Local705FormOfPayment.fullJointAndSurvivorWithPopup,
        policyHolderFactor: 0.8,
        survivorFactor: 1.0,
        tooltip:
            "A 100% Joint & Survivor plan, and if your spouse dies before you, you will receive your life only benefit.",
        needsSpouse: true,
    },
    {
        label: "Life Only with Death Benefit",
        value: Local705FormOfPayment.lifeOnlyWithDeathBenefit,
        policyHolderFactor: 0.0,
        survivorFactor: 1.0,
        tooltip:
            "Monthly payments will be made to you for your lifetime. If you die before 120 monthly pension payments have been made, the remainder of the 120 monthly payments will be paid to your spouse or dependent child(ren).",
        needsSpouse: false,
    },
    {
        label: "Partial Lump Sum",
        value: Local705FormOfPayment.partialLumpSum,
        policyHolderFactor: 0.0,
        survivorFactor: 0.0,
        tooltip: "A partial lump sum.",
        needsSpouse: false,
    },
    {
        label: "Single Life Only",
        value: Local145FormOfPayment.lifeOnly,
        policyHolderFactor: 1,
        survivorFactor: 0,
        tooltip:
            "A life only payment that ends upon your death.  Under this option, all your contributions to the fund will be paid back within 5 years of commencing your benefit.",
        needsSpouse: false,
    },
    {
        label: "Partial Lump Sum",
        value: Local145FormOfPayment.partialLumpSum,
        policyHolderFactor: 0.0,
        survivorFactor: 0.0,
        tooltip: "A partial lump sum.",
        needsSpouse: false,
    },
]

abstract class CommonPensionCalculations<T extends ICommonPensionInputs>
    implements TX.ITransactionGenerator
{
    public paymentsPropertyName: keyof T = "beginPaymentsAge"

    constructor(
        protected profile: Profile,
        protected pensionText: string,
        paymentsPropertyName: keyof T = "beginPaymentsAge"
    ) {
        this.paymentsPropertyName = paymentsPropertyName
    }

    calculationsMayProceed(person: IRetirementPerson, vals: T): boolean {
        return (
            isValidPerson(person) && !_.isNil(vals[this.paymentsPropertyName])
        )
    }

    /**
     * Calculates a simple benefit stream.
     *
     * @param when - the date for which the payment information is requested
     * @param baseAmount - the basic pension monthly amount
     * @param kind - the form of payment
     */
    calculateSimplePension(
        when: moment.Moment,
        baseAmount: number,
        kind: SimplePensionFormOfPayment,
        useTableFactor: boolean
    ) {
        const person = this.getPerson()
        const theirBetterHalf = this.profile.getBetterHalf(person)
        const inputs = this.getPension()
        let amount = 0

        if (isValidPerson(person)) {
            // somebody has to be alive in order to receive a benefit...
            if (
                DateCalcs.isAlive(person, when) ||
                DateCalcs.isAlive(theirBetterHalf, when)
            ) {
                const fop = FormOfPaymentOptions.find((q) => q.value === kind)
                let fac = useTableFactor ? fop.policyHolderFactor : 1
                amount = baseAmount * fac

                // if the person is dead, but theirBetterHalf is alive, pay a reduced amount
                if (
                    DateCalcs.personIsDeadButIsSurvivedBy(
                        when,
                        person,
                        theirBetterHalf
                    )
                ) {
                    fac = useTableFactor ? fop.survivorFactor : 1
                    amount = amount * fac
                    // tslint:disable-next-line:no-debugger
                    //debugger
                    // tslint:disable-next-line:no-console
                    //console.log('simplePension- further reduction on ' + when.format('MM/DD/YYYY') + ' by ' + JSON.stringify(fop) + ' to ' + amount)
                }
            } else {
                //debug('on ' + when.format('MMM YYYY') + ' both are dead ' + person.firstName + theirBetterHalf.firstName)
            }
        }

        const beginPaymentsAge: moment.Duration = _.get(
            inputs,
            this.paymentsPropertyName
        )
        return {
            beginPaymentsAge,
            amount,
            kind,
            totalLumpSum: 0,
            totalVestingService: 0,
            totalPensionCredits: 0,
        }
    }

    /**
     * Gets the monthly pension amount for the person and pension specified.
     *
     * @param person
     * @param vals
     */
    abstract getMonthlyPensionAmount(
        person: IRetirementPerson,
        vals: T,
        when: moment.Moment
    ): Promise<IMonthlyPensionPayments>

    /**
     * Returns the pension object for this pension type.
     */
    abstract getPension(): T

    abstract getPerson(): IRetirementPerson

    /**
     * Returns a list of notable transactions for the timeline.
     */
    getTimeLineTransactions(): Promise<TX.ITransactionItem[]> {
        const person = this.getPerson()
        const personPension = this.getPension()
        if (
            !person ||
            !personPension ||
            !this.calculationsMayProceed(person, personPension)
        ) {
            return Promise.resolve([])
        }

        const paymentsAge: moment.Duration = _.get(
            personPension,
            this.paymentsPropertyName
        )

        const when = dateAtAge(person, paymentsAge)
        let kindText = ""
        if (_.hasIn(personPension, "kind")) {
            const k = (personPension as any).kind as SimplePensionFormOfPayment
            kindText =
                " (" +
                FormOfPaymentOptions.find((fopo) => fopo.value === k).label +
                ")"
        } else if (_.hasIn(personPension, "formOfPayment")) {
            const k = (personPension as any)
                .formOfPayment as SimplePensionFormOfPayment
            const fop = FormOfPaymentOptions.find((fopo) => fopo.value === k)
            let fopText = "unknown"
            if (fop) {
                fopText = fop.label
            } else {
                console.warn(
                    `Unable to find fop '${k}'. pensionPerson: ${JSON.stringify(
                        personPension
                    )}`
                )
                return Promise.resolve([])
            }
            kindText = " (" + fopText + ")"
        } else {
            // tslint:disable-next-line:no-debugger
            //debugger
        }

        return this.getMonthlyPensionAmount(person, personPension, when).then(
            (mpa) => {
                const r: TX.ITransactionItem[] = []
                if (mpa.amount) {
                    r.push({
                        when,
                        section: "Pension",
                        source: this.pensionText,
                        who: person.firstName,
                        what: {
                            pensionStarts: mpa.amount,
                        },
                        description:
                            person.firstName +
                            " begins " +
                            this.pensionText +
                            " pension benefits" +
                            kindText +
                            " at age " +
                            utils.toNiceAge(mpa.beginPaymentsAge, {
                                showMonths: false,
                            }) +
                            " of <a>" +
                            utils.toCurrency(mpa.amount) +
                            "</a>",
                    })
                }
                return r
            }
        )
    }

    /**
     * Computes a set of balance sheet transactions(likely one) for a specific date.
     *
     * @param when The desired date for the requested transaction(s)
     * @param tolerance The desired tolerance for returning the transactions
     */
    getTransactions(
        when: moment.Moment,
        tolerance: TX.TransactionTolerance = "month"
    ): Promise<TX.ITransactionItem[]> {
        const r: TX.ITransactionItem[] = []

        const workers: Array<Promise<boolean>> = []

        do {
            const person = this.getPerson()
            const personPension = this.getPension()
            if (!person || !personPension || !isValidPerson(person)) {
                break
            }

            const paymentsAge: moment.Duration = _.get(
                personPension,
                this.paymentsPropertyName
            )
            const startDate = dateAtAge(person, paymentsAge)
            if (when.isBefore(startDate)) {
                break
            }
            //if (!DateCalcs.isAlive(person, when)) { break }

            const w = this.getMonthlyPensionAmount(
                person,
                personPension,
                when
            ).then((pp) => {
                if (0 === pp.amount) {
                    return false
                }
                const kind = SimplePensionFormOfPayment[pp.kind]
                const desc = DateCalcs.isAlive(person, when)
                    ? " pension benefit at age " +
                      utils.toNiceAge(ageAtAsDuration(person, when))
                    : " survivor benefit"
                r.push({
                    when,
                    section: "Pension",
                    source: this.pensionText,
                    who: person.firstName,
                    what: { [kind]: pp.amount },
                    description:
                        utils.personPossessive(person.firstName) +
                        " " +
                        this.pensionText +
                        desc,
                })
                // tslint:disable-next-line:no-debugger
                //debugger;
                return true
            })
            workers.push(w)
        } while (false)

        return Promise.all(workers).then(() => r)
    }
}

export {
    defaultMonthlyPensionPayment,
    FormOfPaymentOptions,
    CommonPensionCalculations,
}



// WEBPACK FOOTER //
// ./src/app/calcs/pensions/index.ts